// src/utils/pulseService.js
import { db } from '../firebase';
import { doc, updateDoc, increment, getDoc, getDocs, collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';

/**
 * Increment a user's pulse score
 * @param {string} userId - The user's UID
 * @param {number} points - Points to add (e.g., 5 for post, 30 for competition)
 * @returns {Promise<void>}
 */
import { getFunctions, httpsCallable } from 'firebase/functions';

/**
 * Increment a user's pulse score (Secure Server-Side Call)
 * @param {string} userId - The user's UID
 * @param {number} points - Points to add
 * @returns {Promise<void>}
 */
export const incrementPulse = async (userId, points) => {
  try {
    const functions = getFunctions();
    const incrementPulseFn = httpsCallable(functions, 'incrementPulse');
    
    await incrementPulseFn({ 
      userId, 
      points, 
      reason: 'client_action' 
    });
    
    console.log(`✅ Pulse incremented by ${points} via Cloud Function`);
  } catch (error) {
    console.error('Error incrementing pulse (Cloud Function):', error);
  }
};

/**
 * Get a user's current pulse score
 * @param {string} userId - The user's UID
 * @returns {Promise<number>} - The user's pulse score (0 if not set)
 */
export const getUserPulse = async (userId) => {
  try {
    if (!userId) return 0;

    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (userSnap.exists()) {
      return userSnap.data().pulse || 0;
    }
    return 0;
  } catch (error) {
    console.error('Error getting user pulse:', error);
    return 0;
  }
};

/**
 * Subscribe to real-time updates of a user's pulse
 * @param {string} userId - The user's UID
 * @param {Function} callback - Callback function that receives the pulse value
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToUserPulse = (userId, callback) => {
  if (!userId) {
    callback(0);
    return () => { };
  }

  const userRef = doc(db, 'users', userId);

  const unsubscribe = onSnapshot(userRef, (docSnap) => {
    if (docSnap.exists()) {
      const pulse = docSnap.data().pulse || 0;
      callback(pulse);
    } else {
      callback(0);
    }
  }, (error) => {
    console.error('Error subscribing to user pulse:', error);
    callback(0);
  });

  return unsubscribe;
};

/**
 * Subscribe to real-time leaderboard (top users by pulse)
 * @param {number} topN - Number of top users to fetch (default: 10)
 * @param {Function} callback - Callback function that receives array of user objects
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToLeaderboard = (topN = 10, callback) => {
  const usersRef = collection(db, 'users');
  const q = query(usersRef, orderBy('pulse', 'desc'), limit(topN));

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const users = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(users);
  }, (error) => {
    console.error('Error subscribing to leaderboard:', error);
    callback([]);
  });

  return unsubscribe;
};

/**
 * Get top users by pulse (one-time fetch)
 * @param {number} topN - Number of top users to fetch (default: 10)
 * @returns {Promise<Array>} - Array of user objects
 */
export const getTopUsersByPulse = async (topN = 10) => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, orderBy('pulse', 'desc'), limit(topN));

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting top users:', error);
    return [];
  }
};
